<template>
    <div class="kb-content">
        <section
            v-if="seasonsLoaded && seasons.length > 0"
            class="kb-content__coverage"
        >
            <kb-seasons-bar
                :seasons="seasons"
                :variants="article.variants || []"
                @add-override="addSeasonalVariant"
            />
        </section>

        <div
            class="box kb-content__variant"
            :class="{
                'kb-content__variant--pending-deletion': article.will_be_deleted,
                'kb-content__variant--active': isDefaultActive,
            }"
        >
            <div class="kb-content__variant-header">
                <div class="kb-content__variant-title">
                    <span class="kb-content__season-chip kb-content__season-chip--default">
                        {{ $t('knowledgebase.defaultAllYear') }}
                    </span>
                    <span class="kb-content__variant-context">
                        {{ $t('knowledgebase.defaultBodyHint') }}
                    </span>
                    <span v-if="isDefaultActive" class="kb-content__live-tag">
                        {{ $t('knowledgebase.liveNow') }}
                    </span>
                </div>
            </div>

            <div
                class="control"
                :class="{'kb-content__body--pending-deletion': article.will_be_deleted}"
            >
                <kb-markdown-editor
                    v-model="bodyField"
                    height="180px"
                    :has-draft="bodyHasDraft"
                    :can-revert="bodyHasDraft && bodyIsPublished"
                    @blur="saveBody"
                    @revert="revertBody"
                />
            </div>
        </div>

        <section class="kb-content__overrides">
            <h3 class="kb-content__section-headline">
                {{ $t('knowledgebase.seasonalOverrides') }}
                <span class="kb-content__overrides-count" v-if="seasonalVariants.length">
                    · {{ seasonalVariants.length }}
                </span>
            </h3>
            <p class="kb-content__overrides-blurb" v-if="seasonalVariants.length === 0 && seasons.length > 0">
                {{ $t('knowledgebase.seasonalOverridesBlurb') }}
            </p>

            <div
                v-for="variant in seasonalVariants"
                :key="variant.id"
                class="box kb-content__variant"
                :class="{
                    'kb-content__variant--pending-deletion': variant.will_be_deleted,
                    'kb-content__variant--active': variant.id === activeVariantId,
                }"
            >
                <div class="kb-content__variant-header">
                    <div class="kb-content__variant-title">
                        <span
                            class="kb-content__season-chip"
                            :style="{
                                background: seasonColor(variant.season),
                                color: pillTextColor(seasonColor(variant.season)),
                            }"
                        >
                            {{ seasonName(variant.season) }}
                        </span>
                        <span class="kb-content__variant-context">
                            {{ $t('knowledgebase.seasonalOverrideContext', {season: seasonName(variant.season)}) }}
                        </span>
                        <span v-if="variant.id === activeVariantId" class="kb-content__live-tag">
                            {{ $t('knowledgebase.liveNow') }}
                        </span>
                    </div>
                    <div class="kb-content__variant-actions">
                        <button
                            v-if="variant.will_be_deleted"
                            class="button is-small is-warning is-outlined"
                            @click="cancelVariantDeletion(variant)"
                        >
                            <span class="icon is-small"><i class="fa fa-undo" /></span>
                            <span>{{ $t('knowledgebase.cancelDeletion') }}</span>
                        </button>
                        <button
                            v-else
                            class="button is-small is-danger is-outlined"
                            @click="confirmDeleteVariant(variant)"
                        >
                            <span class="icon is-small"><i class="fa fa-trash" /></span>
                            <span>{{ $t('knowledgebase.removeOverride') }}</span>
                        </button>
                    </div>
                </div>

                <div
                    v-if="variant.will_be_deleted"
                    class="notification is-warning is-light kb-content__variant-banner"
                >
                    {{ $t('knowledgebase.variantPendingDeletion') }}
                </div>

                <div
                    class="control"
                    :class="{'kb-content__body--pending-deletion': variant.will_be_deleted}"
                >
                    <kb-markdown-editor
                        v-model="bodySeasonal[variant.id]"
                        height="180px"
                        :has-draft="variantBodyHasDraft(variant)"
                        :can-revert="variantBodyHasDraft(variant) && variant.body_published != null"
                        @blur="saveVariantBody(variant)"
                        @revert="revertVariantBody(variant)"
                    />
                </div>
            </div>

            <div
                v-if="seasonsLoaded && seasons.length === 0"
                class="kb-content__empty"
            >
                <p class="kb-content__empty-headline">
                    {{ $t('knowledgebase.noSeasonsHeadline') }}
                </p>
                <p class="kb-content__empty-copy">
                    {{ $t('knowledgebase.noSeasonsCopy') }}
                </p>
                <router-link
                    class="button is-primary is-outlined"
                    :to="{name: 'seasons', params: {lang}}"
                >
                    <span class="icon"><i class="fa fa-calendar" /></span>
                    <span>{{ $t('knowledgebase.openSeasons') }}</span>
                </router-link>
            </div>

            <div
                v-else-if="availableSeasons.length > 0"
                class="kb-content__add-pills"
            >
                <span class="kb-content__add-pills-label">
                    {{ $t('knowledgebase.addSeasonalOverride') }}:
                </span>
                <button
                    v-for="season in availableSeasons"
                    :key="season.id"
                    class="kb-content__season-pill"
                    :disabled="creatingVariant || article.will_be_deleted"
                    :style="{
                        background: season.color || '#cccccc',
                        color: pillTextColor(season.color),
                    }"
                    @click="addSeasonalVariant(season.id)"
                >
                    + {{ season.name }}
                </button>
            </div>
            <p
                v-else-if="seasonsLoaded && seasons.length > 0"
                class="kb-content__overrides-hint"
            >
                {{ $t('knowledgebase.allSeasonsCovered') }}
            </p>
        </section>

        <section class="kb-content__announcements">
            <h3 class="kb-content__section-headline">
                {{ $t('knowledgebase.announcements') }}
                <span class="kb-content__overrides-count" v-if="announcementVariants.length">
                    · {{ announcementVariants.length }}
                </span>
            </h3>
            <p class="kb-content__overrides-blurb" v-if="announcementVariants.length === 0">
                {{ $t('knowledgebase.announcementsBlurb') }}
            </p>

            <div
                v-for="variant in announcementVariants"
                :key="variant.id"
                class="box kb-content__variant kb-content__variant--announcement"
                :class="{
                    'kb-content__variant--pending-deletion': variant.will_be_deleted,
                    'kb-content__variant--active': variant.id === activeVariantId,
                    'kb-content__variant--past': announcementStatus(variant) === 'past',
                }"
            >
                <div class="kb-content__variant-header">
                    <div class="kb-content__variant-title">
                        <span class="kb-content__season-chip kb-content__season-chip--announcement">
                            📣 {{ variant.label || $t('knowledgebase.announcementUntitled') }}
                        </span>
                        <span class="kb-content__variant-context">
                            {{ formatAnnouncementRange(variant) }}
                            ·
                            {{ $tc('knowledgebase.announcementDayCount', announcementDayCount(variant), {count: announcementDayCount(variant)}) }}
                        </span>
                        <span
                            v-if="announcementStatus(variant) === 'upcoming'"
                            class="kb-content__status-pill"
                        >
                            {{ $t('knowledgebase.announcementUpcoming') }}
                        </span>
                        <span
                            v-else-if="announcementStatus(variant) === 'past'"
                            class="kb-content__status-pill kb-content__status-pill--past"
                        >
                            {{ $t('knowledgebase.announcementEnded') }}
                        </span>
                        <span v-if="variant.id === activeVariantId" class="kb-content__live-tag">
                            {{ $t('knowledgebase.liveNow') }}
                        </span>
                    </div>
                    <div class="kb-content__variant-actions">
                        <button
                            v-if="variant.will_be_deleted"
                            class="button is-small is-warning is-outlined"
                            @click="cancelAnnouncementDeletion(variant)"
                        >
                            <span class="icon is-small"><i class="fa fa-undo" /></span>
                            <span>{{ $t('knowledgebase.cancelDeletion') }}</span>
                        </button>
                        <button
                            v-else
                            class="button is-small is-danger is-outlined"
                            @click="confirmDeleteAnnouncement(variant)"
                        >
                            <span class="icon is-small"><i class="fa fa-trash" /></span>
                            <span>{{ $t('knowledgebase.removeAnnouncement') }}</span>
                        </button>
                    </div>
                </div>

                <div
                    v-if="variant.will_be_deleted"
                    class="notification is-warning is-light kb-content__variant-banner"
                >
                    {{ $t('knowledgebase.announcementPendingDeletion') }}
                </div>

                <div
                    class="control"
                    :class="{'kb-content__body--pending-deletion': variant.will_be_deleted}"
                >
                    <kb-markdown-editor
                        v-model="bodyAnnouncements[variant.id]"
                        height="180px"
                        :has-draft="variantBodyDraftFor(variant)"
                        :can-revert="variantBodyDraftFor(variant) && variant.body_published != null"
                        @blur="saveAnnouncementBody(variant)"
                        @revert="revertAnnouncementBody(variant)"
                    />
                </div>
            </div>

            <button
                class="button is-primary is-outlined kb-content__add-announcement"
                :disabled="article.will_be_deleted"
                @click="openCreateAnnouncement"
            >
                <span class="icon"><i class="fa fa-plus" /></span>
                <span>{{ $t('knowledgebase.addAnnouncement') }}</span>
            </button>
        </section>

        <p class="kb-content__saving" v-if="saving">{{ $t('knowledgebase.saving') }}</p>

        <kb-prompt-modal ref="prompt" />
        <kb-announcement-create-modal ref="announcementModal" />
    </div>
</template>

<script>
import { Api } from '../../../api';
import KbAnnouncementCreateModal from './kb-announcement-create-modal.vue';
import KbMarkdownEditor from './kb-markdown-editor.vue';
import KbPromptModal from './kb-prompt-modal.vue';
import KbSeasonsBar from './kb-seasons-bar.vue';

// Article content tab: default body, seasonal overrides, announcements.
// The shell owns the article fetch + delete + title; this component owns
// the per-variant editing state (`bodyField`, `bodySeasonal`,
// `bodyAnnouncements`) and emits `@changed` after every mutation so the
// shell can refetch and re-distribute.
//
// Per-component prompt/announcement modals are intentional: they're cheap
// $refs and they avoid threading callbacks through the shell.

export default {
    name: 'kb-article-content',

    components: {
        KbAnnouncementCreateModal,
        KbMarkdownEditor,
        KbPromptModal,
        KbSeasonsBar,
    },

    props: {
        article: {type: Object, required: true},
        seasons: {type: Array, required: true},
        seasonsLoaded: {type: Boolean, required: true},
        lang: {type: String, required: true},
    },

    data() {
        return {
            // Local input buffers. Kept in sync with the article via
            // `syncFromArticle()`, called whenever the article prop changes.
            bodyField: '',
            bodySeasonal: {},
            bodyAnnouncements: {},
            saving: false,
            creatingVariant: false,
        };
    },

    computed: {
        defaultVariant() {
            return (this.article.variants || []).find(
                v => v.season == null && v.valid_from == null,
            );
        },
        bodyHasDraft() {
            return this.defaultVariant && this.defaultVariant.body_draft != null;
        },
        bodyIsPublished() {
            return this.defaultVariant && this.defaultVariant.body_published != null;
        },
        seasonsById() {
            const map = {};
            for(const s of this.seasons) map[s.id] = s;
            return map;
        },
        seasonalVariants() {
            const list = (this.article.variants || []).filter(
                v => v.season != null && v.valid_from == null,
            );
            return list.slice().sort((a, b) => {
                const oa = (this.seasonsById[a.season] || {}).order || 0;
                const ob = (this.seasonsById[b.season] || {}).order || 0;
                return oa - ob;
            });
        },
        announcementVariants() {
            const list = (this.article.variants || []).filter(v => v.valid_from != null);
            return list.slice().sort((a, b) => {
                if(a.valid_from < b.valid_from) return -1;
                if(a.valid_from > b.valid_from) return 1;
                return a.id - b.id;
            });
        },
        todayISO() {
            const d = new Date();
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        },
        activeAnnouncement() {
            const t = this.todayISO;
            return this.announcementVariants.find(
                a => !a.will_be_deleted && a.valid_from <= t && a.valid_to >= t,
            ) || null;
        },
        availableSeasons() {
            const used = new Set(this.seasonalVariants.map(v => v.season));
            return this.seasons.filter(s => !used.has(s.id));
        },
        activeSeasonId() {
            const today = new Date();
            today.setHours(12, 0, 0, 0);
            const ts = today.getTime();
            for(const s of this.seasons) {
                for(const p of (s.time_periods || [])) {
                    const start = this._parseDate(p.date_start);
                    const end = this._parseDate(p.date_end);
                    if(!start || !end) continue;
                    end.setHours(23, 59, 59, 999);
                    if(ts >= start.getTime() && ts <= end.getTime()) return s.id;
                }
            }
            return null;
        },
        activeVariantId() {
            if(this.activeAnnouncement) return this.activeAnnouncement.id;
            const id = this.activeSeasonId;
            if(id == null) return null;
            const v = this.seasonalVariants.find(x => x.season === id);
            return v ? v.id : null;
        },
        isDefaultActive() {
            return this.activeVariantId == null;
        },
    },

    watch: {
        article: {
            immediate: true,
            handler() {
                this.syncFromArticle();
            },
        },
    },

    methods: {
        syncFromArticle() {
            const v = this.defaultVariant;
            this.bodyField = v ? (v.body == null ? '' : v.body) : '';

            const next = {};
            const nextAnn = {};
            for(const sv of (this.article.variants || [])) {
                if(sv.season != null && sv.valid_from == null) {
                    next[sv.id] = sv.body == null ? '' : sv.body;
                } else if(sv.valid_from != null) {
                    nextAnn[sv.id] = sv.body == null ? '' : sv.body;
                }
            }
            this.bodySeasonal = next;
            this.bodyAnnouncements = nextAnn;
        },
        async _withSaving(fn) {
            this.saving = true;
            try {
                await fn();
                this.$emit('changed');
            } finally {
                this.saving = false;
            }
        },
        _parseDate(s) {
            if(!s) return null;
            const parts = s.split('-');
            if(parts.length !== 3) return null;
            return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        },
        seasonName(id) {
            const s = this.seasonsById[id];
            return s ? s.name : '';
        },
        seasonColor(id) {
            const s = this.seasonsById[id];
            return s && s.color ? s.color : '#cccccc';
        },
        pillTextColor(hex) {
            if(!hex) return '#333';
            const h = hex.replace('#', '');
            if(h.length !== 6) return '#333';
            const r = parseInt(h.slice(0, 2), 16);
            const g = parseInt(h.slice(2, 4), 16);
            const b = parseInt(h.slice(4, 6), 16);
            const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            return lum > 0.6 ? '#333' : '#fff';
        },
        variantBodyHasDraft(variant) {
            return variant.body_draft != null;
        },
        variantBodyDraftFor(variant) {
            return variant.body_draft != null;
        },
        async saveBody() {
            const variant = this.defaultVariant;
            const current = variant.body || '';
            if(this.bodyField === current) return;
            await this._withSaving(() => Api.knowledgebase.updateVariant(variant.id, {
                body_draft: this.bodyField,
            }));
        },
        async saveVariantBody(variant) {
            const next = this.bodySeasonal[variant.id] || '';
            const current = variant.body || '';
            if(next === current) return;
            await this._withSaving(() => Api.knowledgebase.updateVariant(variant.id, {
                body_draft: next,
            }));
        },
        async revertBody() {
            const variant = this.defaultVariant;
            if(!variant) return;
            await this._withSaving(() => Api.knowledgebase.discardVariantDraft(variant.id));
        },
        async revertVariantBody(variant) {
            await this._withSaving(() => Api.knowledgebase.discardVariantDraft(variant.id));
        },
        async addSeasonalVariant(seasonId) {
            if(seasonId == null || this.creatingVariant) return;
            this.creatingVariant = true;
            try {
                await Api.knowledgebase.createVariant({
                    article: this.article.id,
                    season: seasonId,
                    body_draft: '',
                });
                this.$emit('changed');
            } finally {
                this.creatingVariant = false;
            }
        },
        async saveAnnouncementBody(variant) {
            const next = this.bodyAnnouncements[variant.id] || '';
            const current = variant.body || '';
            if(next === current) return;
            await this._withSaving(() => Api.knowledgebase.updateVariant(variant.id, {
                body_draft: next,
            }));
        },
        async revertAnnouncementBody(variant) {
            await this._withSaving(() => Api.knowledgebase.discardVariantDraft(variant.id));
        },
        async openCreateAnnouncement() {
            if(this.article.will_be_deleted) return;
            await this.$refs.announcementModal.open({
                submitter: async(payload) => {
                    const res = await Api.knowledgebase.createVariant({
                        article: this.article.id,
                        valid_from: payload.valid_from,
                        valid_to: payload.valid_to,
                        label: payload.label,
                        body_draft: '',
                    });
                    if(!res || res.status < 200 || res.status >= 300) {
                        const data = res && res.body;
                        let msg = null;
                        if(data) {
                            if(Array.isArray(data)) msg = data[0];
                            else if(typeof data === 'string') msg = data;
                            else {
                                for(const k of Object.keys(data)) {
                                    const v = data[k];
                                    msg = Array.isArray(v) ? v[0] : v;
                                    if(msg) break;
                                }
                            }
                        }
                        throw new Error(msg || this.$t('knowledgebase.announcementCreateError'));
                    }
                    this.$emit('changed');
                },
            });
        },
        async confirmDeleteAnnouncement(variant) {
            const isLive = variant.body_published != null;
            const message = isLive
                ? this.$t('knowledgebase.confirmDeleteAnnouncementLive')
                : this.$t('knowledgebase.confirmDeleteAnnouncementDraftOnly');
            const ok = await this.$refs.prompt.confirm({
                title: this.$t('knowledgebase.deleteAnnouncement'),
                message,
                submitText: isLive
                    ? this.$t('knowledgebase.stageDeletion')
                    : this.$t('knowledgebase.delete'),
                danger: true,
            });
            if(!ok) return;
            await Api.knowledgebase.deleteVariant(variant.id);
            this.$emit('changed');
        },
        async cancelAnnouncementDeletion(variant) {
            await Api.knowledgebase.restoreVariant(variant.id);
            this.$emit('changed');
        },
        announcementStatus(variant) {
            const t = this.todayISO;
            if(variant.valid_to < t) return 'past';
            if(variant.valid_from > t) return 'upcoming';
            return 'active';
        },
        announcementDayCount(variant) {
            const [y1, m1, d1] = variant.valid_from.split('-').map(Number);
            const [y2, m2, d2] = variant.valid_to.split('-').map(Number);
            const a = new Date(y1, m1 - 1, d1);
            const b = new Date(y2, m2 - 1, d2);
            return Math.round((b - a) / 86400000) + 1;
        },
        formatAnnouncementRange(variant) {
            const fmt = new Intl.DateTimeFormat(undefined, {month: 'short', day: 'numeric'});
            const [y1, m1, d1] = variant.valid_from.split('-').map(Number);
            const [y2, m2, d2] = variant.valid_to.split('-').map(Number);
            return `${fmt.format(new Date(y1, m1 - 1, d1))} – ${fmt.format(new Date(y2, m2 - 1, d2))}`;
        },
        async confirmDeleteVariant(variant) {
            const isLive = variant.body_published != null;
            const message = isLive
                ? this.$t('knowledgebase.confirmDeleteVariantLive')
                : this.$t('knowledgebase.confirmDeleteVariantDraftOnly');
            const ok = await this.$refs.prompt.confirm({
                title: this.$t('knowledgebase.removeOverride'),
                message,
                submitText: isLive
                    ? this.$t('knowledgebase.stageDeletion')
                    : this.$t('knowledgebase.delete'),
                danger: true,
            });
            if(!ok) return;
            await Api.knowledgebase.deleteVariant(variant.id);
            this.$emit('changed');
        },
        async cancelVariantDeletion(variant) {
            await Api.knowledgebase.restoreVariant(variant.id);
            this.$emit('changed');
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../variables';

.kb-content__body--pending-deletion {
    @include kb-pending-deletion;
    position: relative;
    pointer-events: none;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: repeating-linear-gradient(
            -45deg,
            transparent 0,
            transparent 16px,
            rgba(120, 120, 120, 0.18) 16px,
            rgba(120, 120, 120, 0.18) 18px
        );
        pointer-events: none;
    }
}

.kb-content__saving {
    font-size: 0.85rem;
    color: #888;
    margin-top: 8px;
}

.kb-content__coverage {
    margin: 22px 0 28px;
}

.kb-content__section-headline {
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #666;
    margin: 22px 0 8px;
}

.kb-content__overrides {
    margin-top: 8px;
}

.kb-content__overrides-count {
    color: #999;
    font-weight: 400;
}

.kb-content__overrides-blurb {
    font-size: 0.9rem;
    color: #777;
    margin-bottom: 14px;
}

.kb-content__overrides-hint {
    margin-top: 6px;
    font-size: 0.85rem;
    color: #999;
}

.kb-content__variant {
    margin-bottom: 14px;
    transition: box-shadow 0.15s, border-color 0.15s;
}

.kb-content__variant--active {
    box-shadow: 0 0 0 2px $mc-primary, 0 2px 8px rgba(0, 155, 186, 0.12);
}

.kb-content__live-tag {
    margin-left: auto;
    padding: 2px 8px;
    border-radius: 999px;
    background: $mc-primary;
    color: #fff;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
}

.kb-content__variant-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
}

.kb-content__variant-title {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
    font-size: 0.95rem;
}

.kb-content__variant-context {
    font-size: 0.85rem;
    color: #888;
    font-weight: 400;
    margin-left: 4px;
}

.kb-content__season-chip {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: 999px;
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;

    &--default {
        background: #f1f3f5;
        color: #444;
        border: 1px dashed #c5c8cc;
    }

    &--announcement {
        background: #fbe5ee;
        color: #8b1a52;
        border: 1px solid #c44b8a;
    }
}

.kb-content__variant-actions {
    flex-shrink: 0;
}

.kb-content__variant-banner {
    padding: 6px 12px;
    margin-bottom: 10px;
    font-size: 0.85rem;
}

.kb-content__variant--pending-deletion {
    @include kb-pending-deletion('.kb-content__variant-title strong');
}

.kb-content__empty {
    background: #fafafa;
    border: 1px dashed #ddd;
    border-radius: 6px;
    padding: 18px 20px;
    margin-top: 10px;
}

.kb-content__empty-headline {
    font-weight: 600;
    margin-bottom: 6px;
}

.kb-content__empty-copy {
    color: #666;
    margin-bottom: 12px;
    font-size: 0.92rem;
}

.kb-content__add-pills {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
}

.kb-content__add-pills-label {
    font-size: 0.85rem;
    color: #777;
    margin-right: 4px;
}

.kb-content__announcements {
    margin-top: 24px;
}

.kb-content__variant--announcement {
    border-left: 3px solid #c44b8a;
}

.kb-content__variant--past {
    opacity: 0.7;
}

.kb-content__status-pill {
    padding: 2px 8px;
    border-radius: 999px;
    background: #eef0f3;
    color: #555;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;

    &--past {
        opacity: 0.6;
    }
}

.kb-content__add-announcement {
    margin-top: 10px;
}

.kb-content__season-pill {
    appearance: none;
    border: none;
    border-radius: 999px;
    padding: 6px 14px;
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
    transition: opacity 0.15s, transform 0.15s;

    &:hover:not(:disabled) {
        opacity: 0.9;
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-article-content.vue